import { CommonModule } from '@angular/common';
import { Component, input, computed } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { InputErrors } from './models/field-error.interface';
import { formatDatePrimeNG } from './services/field-error-utils';

@Component({
  selector: 'app-field-errors',
  imports: [CommonModule, TranslatePipe],
  template: `
    @if (shouldShowErrors()) {
      @if (errors()?.email) {
        <div class="text-red-500 text-xs mt-1">
          {{ 'common.errors.email' | translate }}
        </div>
      }
      @if (errors()?.minlength) {
        <div class="text-red-500 text-xs mt-1">
          {{
            'common.errors.minlength'
              | translate
                : {
                    requiredLength: errors()?.minlength?.requiredLength,
                    actualLength: errors()?.minlength?.actualLength,
                  }
          }}
        </div>
      }
      @if (errors()?.required) {
        <div class="text-red-500 text-xs mt-1">
          {{ 'common.errors.required' | translate }}
        </div>
      }
      @if (errors()?.maxlength) {
        <div class="text-red-500 text-xs mt-1">
          {{
            'common.errors.maxlength'
              | translate
                : {
                    requiredLength: errors()?.maxlength?.requiredLength,
                    actualLength: errors()?.maxlength?.actualLength,
                  }
          }}
        </div>
      }
      @if (errors()?.pattern) {
        <div class="text-red-500 text-xs mt-1">
          @if (errors()?.pattern?.message) {
            {{
              errors()?.pattern?.message | translate: patternTranslationParams()
            }}
          } @else {
            {{
              'common.errors.pattern'
                | translate
                  : { requiredPattern: errors()?.pattern?.requiredPattern }
            }}
          }
        </div>
      }
      @if (errors()?.min) {
        <div class="text-red-500 text-xs mt-1">
          {{ 'common.errors.min' | translate: { min: errors()?.min?.min } }}
        </div>
      }
      @if (errors()?.max) {
        <div class="text-red-500 text-xs mt-1">
          {{ 'common.errors.max' | translate: { max: errors()?.max?.max } }}
        </div>
      }
      @if (errors()?.step) {
        <div class="text-red-500 text-xs mt-1">
          {{ 'common.errors.step' | translate: { step: errors()?.step?.step } }}
        </div>
      }
      @if (errors()?.equalTo) {
        <div class="text-red-500 text-xs mt-1">
          {{ errors()?.equalTo?.message | translate }}
        </div>
      }
      @if (errors()?.strongPassword) {
        <div class="text-red-500 text-xs mt-1">
          {{ 'common.errors.strongPassword' | translate }}
        </div>
      }
      @if (errors()?.minDate) {
        <div class="text-red-500 text-xs mt-1">
          @if (errors()?.minDate?.message) {
            {{
              errors()?.minDate?.message | translate: minDateTranslationParams()
            }}
          }
        </div>
      }
      @if (errors()?.maxDate) {
        <div class="text-red-500 text-xs mt-1">
          @if (errors()?.maxDate?.message) {
            {{
              errors()?.maxDate?.message | translate: maxDateTranslationParams()
            }}
          }
        </div>
      }
    }
  `,
})
export class FieldErrorsComponent {
  errors = input<InputErrors | null | undefined>(null);

  control = input<any>(null);

  patternTranslationParams = computed(() => {
    const params: Record<string, any> = {};
    const patternError = this.errors()?.pattern;

    if (!patternError) {
      return params;
    }

    if (patternError.durationLabel) {
      params['durationLabel'] = patternError.durationLabel;
    }

    if (patternError.dynamicValues) {
      Object.assign(params, patternError.dynamicValues);
    }

    return params;
  });

  minDateTranslationParams = computed(() => {
    const params: Record<string, any> = {};
    const minDateError = this.errors()?.minDate;

    if (!minDateError) {
      return params;
    }

    if (minDateError.dynamicValues) {
      Object.assign(params, minDateError.dynamicValues);
    }

    params['minDate'] = formatDatePrimeNG(
      minDateError.requiredDate,
      minDateError.format ?? 'dd.mm.yy',
    );

    return params;
  });

  maxDateTranslationParams = computed(() => {
    const params: Record<string, any> = {};
    const maxDateError = this.errors()?.maxDate;

    if (!maxDateError) {
      return params;
    }

    if (maxDateError.dynamicValues) {
      Object.assign(params, maxDateError.dynamicValues);
    }

    params['maxDate'] = formatDatePrimeNG(
      maxDateError.requiredDate,
      maxDateError.format ?? 'dd.mm.yy',
    );

    return params;
  });

  shouldShowErrors(): boolean {
    return this.control()?.touched || this.control()?.dirty;
  }
}
